import { httpClient } from "@/app/client/httpClient";
import { authStore } from "@/store/auth";
import { DisplayDetail } from "@/types/ad/DisplayDetail";
import { DisplaySlot } from "@/types/ad/DisplaySlot";
import { captureException } from "@sentry/vue";
import router from "@/router";

class AdService {
  async displaySlot(slot: string): Promise<DisplaySlot | null> {
    try {
      return await httpClient.system.ad.display_slot.$get({
        query: {
          system_name: "caisuke",
          slot: slot,
          target_key: authStore.user?.username
        }
      });
    } catch (err) {
      captureException(err);
      return null;
    }
  }

  async displayDetail(adId: number): Promise<DisplayDetail | null> {
    try {
      return await httpClient.system.ad.display_detail.$get({
        query: {
          system_name: "caisuke",
          ad_id: adId,
          target_key: authStore.user?.username
        }
      });
    } catch (err) {
      captureException(err);
      return null;
    }
  }

  async registerEvent(
    adId: number,
    eventType: string,
    slotId?: number,
    additionalData?: string
  ) {
    try {
      return await httpClient.system.ad.register_event.$post({
        body: {
          adId: adId,
          userKey: authStore.user?.username ?? authStore.sessionId ?? "",
          eventType: eventType,
          slotId: slotId,
          additionalData: additionalData
        }
      });
    } catch (err) {
      captureException(err);
    }
  }

  async registerClickEvent(
    adId: number,
    clickEventValue: string,
    slotId?: number
  ) {
    await this.registerEvent(
      adId,
      "click",
      slotId,
      JSON.stringify({
        clickValue: clickEventValue
      })
    );
  }

  async handleAdClickEvent(event: Event, adId: number, slotId?: number) {
    const target = event.target as HTMLElement;

    // data-ad-click-event 属性を対象とする
    const clickEventValue = target.dataset.adClickEvent;
    if (clickEventValue) {
      // SPA のルート変更ではなく通常のページ遷移が発生し初期描画が走ってしまうため、<a>タグかどうか確認する
      const linkElement = target.closest(
        "a[data-ad-click-event]"
      ) as HTMLAnchorElement;
      if (linkElement && linkElement.href) {
        const href = linkElement.getAttribute("href");
        if (href && (href.startsWith("/#/") || href.startsWith("#/"))) {
          // 内部リンクの場合
          // デフォルトの動作（ページ遷移）を防ぐ
          event.preventDefault();
          event.stopPropagation();

          // クリックイベントを登録
          await this.registerClickEvent(adId, clickEventValue, slotId);

          // ハッシュを除去してルートパスを取得
          const routePath = href.replace(/^#?\/?(#\/)?/, "/");
          try {
            // SPAルーティングで遷移
            await router.push(routePath);
          } catch (error) {
            // navigation guard 関係のエラーを握りつぶす
            console.error(error);
          }
          return;
        }
      }
      // 無効なhref、外部リンク、<a>タグ以外の要素の場合は通常通り処理
      await this.registerClickEvent(adId, clickEventValue, slotId);
    }
  }
}

export const adService = new AdService();
